import { Check, ClipboardCheck, Pencil, SkipForward, Timer } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { DebriefCard } from "@/components/session/DebriefCard";
import { errText } from "@/components/pipeline/utils";
import "@/session.css";

const formOf = (d) => ({ summary: d.draft?.summary || "", comment: d.draft?.comment || "", script_answers: (d.draft?.script_answers || []).map((a) => ({ question: a.question, answer: a.answer || "", options: a.options || [] })), next_action: d.draft?.next_action || "", next_date: d.draft?.next_date || "", tag: d.tag || "" });
const order = (items) => [...items].sort((a, b) => (a.status === b.status ? 0 : a.status === "ready" ? -1 : 1));
const typing = (e) => ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName);
const chrono = (s) => `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`;

export default function DebriefPage() {
  const session = new URLSearchParams(useLocation().search).get("session");
  const [items, setItems] = useState(null);
  const [curId, setCurId] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [done, setDone] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dx, setDx] = useState(0);
  const [secs, setSecs] = useState(0);
  const touch = useRef(null);
  const load = useCallback(() => api.get("/debriefs", { params: session ? { session_id: session } : {} }).then(({ data }) => setItems(order(data.items))).catch((e) => toast.error(errText(e))), [session]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => setSecs((s) => s + 1), 1000); return () => clearInterval(t); }, []);
  const waiting = items?.filter((i) => i.status === "waiting").length || 0;
  useEffect(() => { if (!waiting) return undefined; const t = setInterval(load, 6000); return () => clearInterval(t); }, [waiting, load]);
  const cur = items?.find((i) => i.id === curId) || items?.[0];
  const formKey = cur ? `${cur.id}:${cur.status}` : "";
  useEffect(() => { if (cur) { setCurId(cur.id); setForm(formOf(cur)); setEditing(false); } }, [formKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const drop = (id) => { setItems((list) => list.filter((i) => i.id !== id)); setCurId(null); setForm(null); };
  const validate = useCallback(async () => {
    if (!cur || cur.status !== "ready" || busy) return;
    setBusy(true);
    try {
      await api.post(`/debriefs/${cur.id}/validate`, { ...form, script_answers: form.script_answers.map(({ question, answer }) => ({ question, answer })), edited: JSON.stringify(form) !== JSON.stringify(formOf(cur)) });
      setDone((n) => n + 1);
      drop(cur.id);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  }, [cur, form, busy]);
  const skip = useCallback(async () => {
    if (!cur || busy) return;
    setBusy(true);
    try { await api.post(`/debriefs/${cur.id}/skip`); setSkipped((n) => n + 1); drop(cur.id); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  }, [cur, busy]);
  const edit = () => { setEditing(true); setTimeout(() => document.querySelector("[data-testid='debrief-summary']")?.focus(), 30); };
  const [refilling, setRefilling] = useState(false);
  const prefill = async () => {
    setRefilling(true);
    try { const { data } = await api.post(`/debriefs/${cur.id}/prefill`); setItems((list) => order(list.map((i) => (i.id === data.id ? { ...i, ...data } : i)))); setForm(formOf(data)); } catch (e) { toast.error(errText(e)); } finally { setRefilling(false); }
  };
  useEffect(() => {
    const onKey = (e) => {
      if (typing(e)) {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); validate(); }
        if (e.key === "Escape") e.target.blur();
        return;
      }
      if (e.key === "Enter" || e.key === "v") { e.preventDefault(); validate(); }
      if (e.key === "m" || e.key === "e") { e.preventDefault(); edit(); }
      if (e.key === "p" || e.key === "ArrowRight") { e.preventDefault(); skip(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [validate, skip]);
  const onTouchEnd = () => { if (dx > 90) validate(); else if (dx < -90) skip(); setDx(0); touch.current = null; };
  if (!items) return <section className="page" data-testid="debrief-page"><p className="pl-muted">Chargement du débrief…</p></section>;
  return (
    <section className="page db-page" data-testid="debrief-page">
      <header className="cs-head">
        <div><p className="eyebrow">DÉBRIEF {session ? "DE LA SESSION" : "DES APPELS"}</p><h1><ClipboardCheck size={24} /> Débrief</h1></div>
        <div className="db-stats" data-testid="debrief-stats"><span><b>{items.length}</b> à traiter</span>{waiting > 0 && <span><b>{waiting}</b> en attente IA</span>}<span><b>{done}</b> validées</span><span><Timer size={13} /> {chrono(secs)}</span></div>
      </header>
      {!cur ? (
        <div className="ck-empty db-done" data-testid="debrief-empty">
          <strong>Tout est débriefé{done ? ` : ${done} fiche${done > 1 ? "s" : ""} en ${chrono(secs)}` : ""}.</strong>
          <p>L'IA prépare maintenant les actions à valider sur ces fiches.</p>
          <Link className="primary-button" to="/cockpit?tab=ai" data-testid="debrief-go-actions">Voir la file d'actions IA</Link>
        </div>
      ) : (
        <article className="db-card" style={{ transform: `translateX(${dx}px) rotate(${dx / 40}deg)` }} data-testid="debrief-card"
          onTouchStart={(e) => { if (!typing(e)) touch.current = e.touches[0].clientX; }} onTouchMove={(e) => touch.current !== null && setDx(e.touches[0].clientX - touch.current)} onTouchEnd={onTouchEnd}>
          <div className={`db-swipe ${dx > 40 ? "ok" : dx < -40 ? "no" : ""}`}>{dx > 40 ? "Valider" : dx < -40 ? "Passer" : ""}</div>
          <p className="cs-pos">Fiche {done + skipped + 1} sur {done + skipped + items.length} · {cur.user_name}</p>
          <h2 data-testid="debrief-title">{cur.title}</h2>
          {form && <DebriefCard d={cur} form={form} setForm={setForm} editing={editing} onPrefill={prefill} refilling={refilling} />}
          <footer className="cs-actions">
            <button type="button" className="secondary-button" disabled={busy} onClick={skip} data-testid="debrief-skip"><SkipForward size={15} /> Passer <kbd>P</kbd></button>
            <button type="button" className="secondary-button" disabled={cur.status !== "ready"} onClick={edit} data-testid="debrief-edit"><Pencil size={15} /> Modifier <kbd>M</kbd></button>
            <button type="button" className="primary-button" disabled={busy || cur.status !== "ready"} onClick={validate} data-testid="debrief-validate"><Check size={15} /> Valider <kbd>Entrée</kbd></button>
          </footer>
          <p className="cs-hint">Sur mobile : glissez à droite pour valider, à gauche pour passer · Ctrl+Entrée valide depuis un champ</p>
        </article>
      )}
    </section>
  );
}
